import { Map, CheckCircle2, MessageSquare, ClipboardCheck, ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { AccordionList } from "../components/AccordionList";
import { TRANSFORMS, TAKEAWAYS, FAQS, scrollToId } from "../lib/content";

const ICONS = { Map, CheckCircle2, MessageSquare, ClipboardCheck };

export const Transformation = () => (
  <section className="py-28 md:py-36 px-6 md:px-12" style={{ background: "#6D28D9" }} data-testid="transformation-section">
    <div className="max-w-[1300px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-24">
      <Reveal>
        <p className="chapter-label mb-6" style={{ color: "rgba(255,255,255,0.65)" }}>[006] — SOUND FAMILIAR?</p>
        <h2 className="font-display font-bold uppercase text-3xl md:text-[40px] leading-[1.05] tracking-tight mb-8 text-white">
          If You're Like Most First-Time Buyers I Work With...
        </h2>
        <div className="space-y-5 text-[15px] leading-[1.8] text-white/75">
          <p>
            You make good money, you pay rent on time every month, and you've done the mental math a hundred times — yet every attempt to get a straight answer ends in a form letter, a "not yet," or a call-center agent reading from a script. You're not lazy. You're not behind. You've just never been given the actual map.
          </p>
          <p>
            Meanwhile, the internet says twenty different things about down payments, your cousin swears you need an 800 credit score, and every calculator gives you a different number. The confusion isn't your fault — the process was never explained to you in plain language. That's what changes here.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="glass-panel p-8 md:p-10 h-full flex flex-col justify-center relative overflow-hidden" style={{ background: "rgba(255,255,255,0.96)" }}>
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: 0.1, maskImage: "linear-gradient(180deg, black 0%, transparent 80%)", WebkitMaskImage: "linear-gradient(180deg, black 0%, transparent 80%)" }}
          />
          <div className="relative">
          <p className="font-mono-c text-[11px] tracking-[0.25em] mb-8" style={{ color: "#6D28D9" }}>
            HERE'S WHAT CHANGES AFTER THIS WEBINAR:
          </p>
          <ul className="space-y-6">
            {TRANSFORMS.map((t, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`transform-item-${i}`}>
                <ArrowRight size={18} className="mt-1 shrink-0" style={{ color: "#7C3AED" }} />
                <span className="text-[15px] leading-relaxed text-black/75">{t}</span>
              </li>
            ))}
          </ul>
          <button onClick={() => scrollToId("register")} className="btn-accent mt-10 px-8 py-4 text-[13px] self-start" data-testid="transformation-cta">
            ✓ RESERVE MY FREE SEAT
          </button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Takeaways = () => (
  <section className="py-28 md:py-36 px-6 md:px-12" style={{ background: "#FFFFFF" }} data-testid="takeaways-section">
    <div className="max-w-[1300px] mx-auto">
    <Reveal>
      <p className="chapter-label mb-6">[007] — THE DELIVERABLES</p>
      <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight mb-14">What You'll Walk Away With</h2>
    </Reveal>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {TAKEAWAYS.map((t, i) => {
        const Icon = ICONS[t.icon];
        return (
          <Reveal key={i} delay={i * 0.07}>
            <div className="p-7 h-full border border-black/10 transition-[border-color] duration-300 hover:border-[#7C3AED]" style={{ background: "rgba(0,0,0,0.02)" }} data-testid={`takeaway-card-${i}`}>
              <Icon size={22} className="mb-5" style={{ color: "#7C3AED" }} />
              <h3 className="font-display font-semibold text-lg mb-2 text-[#181818]">{t.title}</h3>
              <p className="text-[13px] leading-relaxed text-black/45">{t.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    </div>
  </section>
);

export const FAQ = () => (
  <section id="faq" className="py-28 md:py-36 px-6 md:px-12" style={{ background: "#F2EEFB" }} data-testid="faq-section">
    <div className="max-w-[1000px] mx-auto">
    <Reveal>
      <p className="chapter-label mb-6">[008] — COMMON QUESTIONS</p>
      <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight mb-14">Common Questions</h2>
    </Reveal>
    <Reveal delay={0.1}>
      <AccordionList items={FAQS.map((f) => ({ title: f.q, body: f.a }))} testPrefix="faq" />
    </Reveal>
    </div>
  </section>
);
